import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Zap,
  Shield,
  DollarSign,
  Clock,
  Play,
  Pause,
  Sparkles,
  BarChart3,
  Droplets,
} from 'lucide-react';
import type {
  AgenticBundle,
  ProposedTrade,
  ReasoningStep,
  ComplianceCheckResult,
  LiquidityCheck,
} from '../../types/agentic';

interface IntentResultPanelProps {
  bundle: AgenticBundle;
  onExecute: (bundleId: string, simulateOnly?: boolean) => void;
  onDismiss: () => void;
  isExecuting?: boolean;
}

// Styled components using Tailwind classes
const Panel: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    onClick={(e) => e.target === e.currentTarget && onClose()}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      transition={{ duration: 0.2 }}
      className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden bg-slate-900 rounded-2xl border border-white/10 shadow-2xl"
    >
      {children}
    </motion.div>
  </motion.div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    READY_FOR_APPROVAL: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    ANALYZING: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    APPROVED: 'bg-green-500/20 text-green-400 border-green-500/30',
    EXECUTING: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    COMPLETED: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    REJECTED: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${styles[status] || styles.ANALYZING}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const ComplianceStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'COMPLIANT':
      return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    case 'WARNING':
      return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    case 'VIOLATION':
      return <XCircle className="w-4 h-4 text-red-400" />;
    default:
      return <Clock className="w-4 h-4 text-slate-400" />;
  }
};

const LiquidityBadge: React.FC<{ assessment: string }> = ({ assessment }) => {
  const colors: Record<string, string> = {
    EXCELLENT: 'text-emerald-400',
    GOOD: 'text-blue-400',
    MODERATE: 'text-amber-400',
    POOR: 'text-red-400',
  };

  return <span className={`font-medium ${colors[assessment] || 'text-slate-400'}`}>{assessment}</span>;
};

const ReasoningTimeline: React.FC<{ steps: ReasoningStep[] }> = ({ steps }) => {
  const [expanded, setExpanded] = useState(true);

  const stepIcons: Record<string, React.ReactNode> = {
    INTENT_ANALYSIS: <Sparkles className="w-4 h-4" />,
    TRADE_GENERATION: <TrendingUp className="w-4 h-4" />,
    COMPLIANCE_CHECK: <Shield className="w-4 h-4" />,
    LIQUIDITY_CHECK: <Droplets className="w-4 h-4" />,
    COST_ESTIMATION: <DollarSign className="w-4 h-4" />,
    BUNDLE_FINALIZED: <CheckCircle className="w-4 h-4" />,
  };

  return (
    <div className="border border-white/5 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-slate-800/50 hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-violet-400" />
          <span className="font-semibold text-white">Reasoning Timeline</span>
          <span className="text-xs text-slate-500">({steps.length} steps)</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400">
                      {stepIcons[step.stepType] || <Zap className="w-4 h-4" />}
                    </div>
                    {index < steps.length - 1 && (
                      <div className="w-px h-full bg-white/10 my-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white">{step.description}</span>
                      <span className="text-xs text-slate-500">
                        {step.stepType.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">{step.reasoning}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TradeCard: React.FC<{ trade: ProposedTrade }> = ({ trade }) => (
  <div className="p-4 bg-slate-800/50 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <span
          className={`px-2 py-0.5 text-xs font-bold rounded ${
            trade.side === 'BUY'
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-red-500/20 text-red-400'
          }`}
        >
          {trade.side}
        </span>
        <span className="font-medium text-white">{trade.securityName}</span>
      </div>
      <div className="text-right">
        <div className="text-lg font-semibold text-white">
          ${(trade.notional / 1000000).toFixed(2)}M
        </div>
        <div className="text-xs text-slate-500">
          {trade.quantity.toLocaleString()} @ ${trade.price.toFixed(2)}
        </div>
      </div>
    </div>

    <div className="flex items-center gap-4 text-xs text-slate-400 mb-2">
      <span>CUSIP: {trade.securityId}</span>
      {trade.duration && <span>Duration: {trade.duration.toFixed(1)}y</span>}
      {trade.creditRating && <span>Rating: {trade.creditRating}</span>}
      {trade.sector && <span>Sector: {trade.sector}</span>}
    </div>

    <p className="text-sm text-slate-300 mt-2 pt-2 border-t border-white/5">
      {trade.rationale}
    </p>
  </div>
);

const ComplianceSection: React.FC<{ checks: ComplianceCheckResult[] }> = ({ checks }) => {
  const allCompliant = checks.every((c) => c.status === 'COMPLIANT');
  const hasWarnings = checks.some((c) => c.status === 'WARNING');
  const hasViolations = checks.some((c) => c.status === 'VIOLATION');

  return (
    <div className="border border-white/5 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between p-4 bg-slate-800/50">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400" />
          <span className="font-semibold text-white">Compliance Checks</span>
        </div>
        <div className="flex items-center gap-2">
          {allCompliant && (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle className="w-3 h-3" /> All Passed
            </span>
          )}
          {hasWarnings && (
            <span className="flex items-center gap-1 text-xs text-amber-400">
              <AlertTriangle className="w-3 h-3" /> Warnings
            </span>
          )}
          {hasViolations && (
            <span className="flex items-center gap-1 text-xs text-red-400">
              <XCircle className="w-3 h-3" /> Violations
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {checks.map((check, index) => (
          <div key={index} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
            <ComplianceStatusIcon status={check.status} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-white">{check.securityId}</span>
                <span
                  className={`text-xs ${
                    check.status === 'COMPLIANT'
                      ? 'text-emerald-400'
                      : check.status === 'WARNING'
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`}
                >
                  {check.status}
                </span>
              </div>
              <p className="text-sm text-slate-400">{check.reasoningLog}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {check.rulesChecked.map((rule, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 text-xs bg-slate-700/50 text-slate-400 rounded"
                  >
                    {rule}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LiquiditySection: React.FC<{ checks: LiquidityCheck[] }> = ({ checks }) => (
  <div className="border border-white/5 rounded-xl overflow-hidden">
    <div className="flex items-center gap-2 p-4 bg-slate-800/50">
      <Droplets className="w-4 h-4 text-cyan-400" />
      <span className="font-semibold text-white">Liquidity Assessment</span>
    </div>

    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {checks.map((check, index) => (
          <div key={index} className="p-3 bg-slate-900/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">{check.securityName}</span>
              <LiquidityBadge assessment={check.liquidityAssessment} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-500">Score:</span>
                <span className="ml-1 text-white">{(check.liquidityScore * 100).toFixed(0)}%</span>
              </div>
              <div>
                <span className="text-slate-500">Spread:</span>
                <span className="ml-1 text-white">{(check.bidAskSpread * 100).toFixed(2)}%</span>
              </div>
              <div>
                <span className="text-slate-500">MarketAxess:</span>
                <span className="ml-1 text-white">{check.marketAxessQuotes} quotes</span>
              </div>
              <div>
                <span className="text-slate-500">Tradeweb:</span>
                <span className="ml-1 text-white">{check.tradewebQuotes} quotes</span>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">{check.recommendation}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CostSummary: React.FC<{ costs: AgenticBundle['estimatedCosts']; summary: AgenticBundle['summary'] }> = ({
  costs,
  summary,
}) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <div className="p-4 bg-slate-800/50 rounded-xl border border-white/5">
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Notional</div>
      <div className="text-xl font-bold text-white">
        ${(summary.totalNotional / 1000000).toFixed(1)}M
      </div>
      <div className="text-xs text-slate-400">
        {summary.buyTrades} buys, {summary.sellTrades} sells
      </div>
    </div>

    <div className="p-4 bg-slate-800/50 rounded-xl border border-white/5">
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Cost</div>
      <div className="text-xl font-bold text-white">${costs.totalCostDollars.toLocaleString()}</div>
      <div className="text-xs text-slate-400">{costs.totalCostBps.toFixed(1)} bps</div>
    </div>

    <div className="p-4 bg-slate-800/50 rounded-xl border border-white/5">
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Avg Liquidity</div>
      <div className="text-xl font-bold text-white">
        {(summary.avgLiquidityScore * 100).toFixed(0)}%
      </div>
      <div className="text-xs text-emerald-400">Excellent</div>
    </div>

    <div className="p-4 bg-slate-800/50 rounded-xl border border-white/5">
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Est. Execution</div>
      <div className="text-xl font-bold text-white">{summary.estimatedExecutionTime}</div>
      <div className="text-xs text-slate-400">{summary.totalTrades} trades</div>
    </div>
  </div>
);

export const IntentResultPanel: React.FC<IntentResultPanelProps> = ({
  bundle,
  onExecute,
  onDismiss,
  isExecuting = false,
}) => {
  return (
    <Panel onClose={onDismiss}>
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-white/10">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <h2 className="text-xl font-bold text-white">AI-Generated Trade Plan</h2>
            <StatusBadge status={bundle.status} />
          </div>
          <p className="text-slate-400 max-w-2xl">
            <span className="text-white font-medium">"{bundle.intent}"</span>
            {bundle.portfolioName && (
              <span className="text-slate-500"> • Portfolio: {bundle.portfolioName}</span>
            )}
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
        {/* Cost Summary */}
        <CostSummary costs={bundle.estimatedCosts} summary={bundle.summary} />

        {/* Reasoning Timeline */}
        <ReasoningTimeline steps={bundle.reasoningLog} />

        {/* Proposed Trades */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Proposed Trades ({bundle.proposedTrades.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bundle.proposedTrades.map((trade) => (
              <TradeCard key={trade.tradeId} trade={trade} />
            ))}
          </div>
        </div>

        {/* Compliance Checks */}
        <ComplianceSection checks={bundle.complianceChecks} />

        {/* Liquidity Assessment */}
        <LiquiditySection checks={bundle.liquidityChecks} />
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between p-6 border-t border-white/10 bg-slate-900/50">
        <div className="text-sm text-slate-400">
          Bundle ID: <span className="font-mono text-slate-300">{bundle.bundleId}</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onExecute(bundle.bundleId, true)}
            disabled={isExecuting}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Pause className="w-4 h-4" />
            Simulate
          </button>
          <button
            onClick={() => onExecute(bundle.bundleId, false)}
            disabled={isExecuting || bundle.status === 'REJECTED'}
            className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {isExecuting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Executing...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Execute Trades
              </>
            )}
          </button>
        </div>
      </div>
    </Panel>
  );
};

export default IntentResultPanel;
